import {
  Box, Button, Flashbar, Form, FormField, Header, Input, Modal,
  SpaceBetween, Table, Toggle,
} from "@cloudscape-design/components";
import { useCallback, useEffect, useState } from "react";
import { getAuthToken } from "./auth";
import { apiFetch } from "./apiFetch";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { useDocumentTitle } from "./useDocumentTitle";

interface Account {
  id: number;
  documentId: string;
  Alias: string;
  accountid: string;
  publishedAt: string | null;
  isOrganizationMain: boolean;
}

export default function AccountsPage() {
  useDocumentTitle("Accounts");
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Account[]>([]);
  const [flash, setFlash] = useState<{ type: "success" | "error"; content: string } | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [alias, setAlias] = useState("");
  const [accountId, setAccountId] = useState("");
  const [isOrgMain, setIsOrgMain] = useState(false);
  const [saving, setSaving] = useState(false);

  const apiKey = getAuthToken();
  const selected = selectedItems[0];

  const fetchAccounts = useCallback(() => {
    if (!apiKey) { setLoading(false); return; }
    setLoading(true);
    apiFetch(`/accounts`)
      .then((res) => {
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return res.json();
      })
      .then((json) =>
        setAccounts(
          json.data.map((item: any) => ({
            id: item.id,
            documentId: item.documentId,
            Alias: item.Alias,
            accountid: item.accountid,
            publishedAt: item.publishedAt,
            isOrganizationMain: item.isOrganizationMain ?? false,
          }))
        )
      )
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [apiKey]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const openCreate = () => {
    setAlias("");
    setAccountId("");
    setIsOrgMain(false);
    setModalMode("create");
  };

  const openEdit = () => {
    if (!selected) return;
    setAlias(selected.Alias);
    setAccountId(selected.accountid);
    setIsOrgMain(selected.isOrganizationMain);
    setModalMode("edit");
  };

  const handleSave = async () => {
    if (!apiKey) return;
    setSaving(true);
    try {
      const isEdit = modalMode === "edit";
      const url = isEdit ? `/accounts/${selected.documentId}` : `/accounts`;
      const res = await apiFetch(url, {
        method: isEdit ? "PUT" : "POST",
        body: JSON.stringify({ data: { Alias: alias, accountid: accountId, isOrganizationMain: isOrgMain } }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      setFlash({ type: "success", content: isEdit ? "Account updated" : "Account created" });
      setModalMode(null);
      setSelectedItems([]);
      fetchAccounts();
    } catch (e: any) {
      setFlash({ type: "error", content: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!apiKey || !selected) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/accounts/${selected.documentId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      setFlash({ type: "success", content: "Account deleted" });
      setDeleteVisible(false);
      setSelectedItems([]);
      fetchAccounts();
    } catch (e: any) {
      setFlash({ type: "error", content: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SpaceBetween size="l">
      {flash && (
        <Flashbar items={[{ type: flash.type, content: flash.content, dismissible: true, onDismiss: () => setFlash(null) }]} />
      )}
      <Table
        variant="full-page"
        loading={loading}
        items={accounts}
        selectionType="single"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems as Account[])}
        trackBy="documentId"
        columnDefinitions={[
          { id: "alias", header: "Alias", cell: (item) => item.Alias },
          { id: "accountid", header: "Account ID", cell: (item) => item.accountid },
          { id: "orgMain", header: "Org Main", cell: (item) => item.isOrganizationMain ? "Yes" : "—" },
          {
            id: "publishedAt",
            header: "Published At",
            cell: (item) => item.publishedAt ? new Date(item.publishedAt).toLocaleDateString() : "—",
          },
        ]}
        header={
          <Header
            counter={`(${accounts.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button disabled={!selected} onClick={openEdit}>Edit</Button>
                <Button disabled={!selected} onClick={() => setDeleteVisible(true)}>Delete</Button>
                <Button variant="primary" onClick={openCreate}>Create account</Button>
              </SpaceBetween>
            }
          >
            Accounts
          </Header>
        }
        empty={
          <SpaceBetween size="m">
            <Box textAlign="center" color={error ? "text-status-error" : "text-body-secondary"}>
              {error ? `Error: ${error}` : !apiKey ? "No API key configured. Go to Settings to add one." : "No accounts found"}
            </Box>
          </SpaceBetween>
        }
      />
      <Modal
        visible={modalMode !== null}
        onDismiss={() => setModalMode(null)}
        header={modalMode === "edit" ? "Edit account" : "Create account"}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setModalMode(null)}>Cancel</Button>
              <Button variant="primary" onClick={handleSave} loading={saving}>Submit</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween size="m">
            <FormField label="Alias">
              <Input value={alias} onChange={({ detail }) => setAlias(detail.value)} />
            </FormField>
            <FormField label="Account ID">
              <Input value={accountId} onChange={({ detail }) => setAccountId(detail.value)} />
            </FormField>
            <FormField label="Organization main account">
              <Toggle checked={isOrgMain} onChange={({ detail }) => setIsOrgMain(detail.checked)}>
                This is the main organization account
              </Toggle>
            </FormField>
          </SpaceBetween>
        </Form>
      </Modal>
      <DeleteConfirmModal
        visible={deleteVisible}
        header="Delete account"
        itemName={selected?.Alias}
        loading={saving}
        onDismiss={() => setDeleteVisible(false)}
        onConfirm={handleDelete}
      />
    </SpaceBetween>
  );
}
